import React, { useEffect, useRef } from 'react'

import { useConfigurationStore } from '@stores/configurationStore'
import { useAutoplaySliderItems } from './useAutoplaySliderItems'
import AutoplaySliderItem from './AutoplaySliderItem'
import AutoplaySettingsOptionButton from './AutoplaySettingsOptionButton'
import PlayNowButton from './PlayNowButton'
import { useAutoplaySettingsButtons } from './useAutoplaySettingsButtons'
import { AutoplayButtonClickedMessage, GameUIMessageType, ViewportType } from 'game-ui-types'
import { useSpinButtonSectionStore } from '@stores/spinButtonSectionStore'
import { useNavigationStore } from '@stores/navigationStore'
import { useUltraPlayStore } from '@stores/ultraPlayStore'
import { useTranslation } from '@amusnet/translations/react'
import { cn } from '@amusnet/react-ui'
import { useHasActiveBonus, useIsUltraPlayAvailable } from '@hooks'
import { gameAnalytics } from '@utils/gameAnalytics'
import { useSoundStore } from '@stores/soundStore'
import { SOUNDS } from '@constants/sounds'
import ultraPlayLogo from '@assets/ultra-play/ultra-play-logo.svg'
import playNowButtonBg from '@assets/button-playnow.svg'
import { useShrinkTextToFit } from '@amusnet/react-ui'

const AutoplaySettings: React.FC = () => {
    const { t } = useTranslation()
    // Ultra Play keys are pending in Lokalise; widen t() so they compile now and resolve
    // automatically once they land in the synced TranslationKey union.
    const tUltra = t as (key: string) => string
    const items = useAutoplaySliderItems()
    const { buttons, autoplayOptions } = useAutoplaySettingsButtons()
    const currency = useConfigurationStore((state) => state.currency)
    const viewport = useConfigurationStore((state) => state.viewport)
    const selectedAutoplayIndex = useSpinButtonSectionStore((state) => state.selectedAutoplayIndex)
    const updateSelectedAutoplayIndex = useSpinButtonSectionStore((state) => state.updateSelectedAutoplayIndex)
    const updateAutoplayCount = useSpinButtonSectionStore((state) => state.updateAutoplayCount)
    const updateAutoplayMode = useSpinButtonSectionStore((state) => state.updateAutoplayMode)
    const setIsMenuOpen = useNavigationStore((state) => state.setIsMenuOpen)
    const playEngineSound = useSoundStore((state) => state.playEngineSound)
    const isUltraPlayAvailable = useIsUltraPlayAvailable()
    const openUltraPlay = useUltraPlayStore((state) => state.open)
    const shouldShowSpeedButton = useConfigurationStore((state) => state.shouldShowSpeedButton)
    // Bet is locked during a bonus, so the Ultra Play entry is disabled then.
    const hasActiveBonus = useHasActiveBonus()
    const shouldOpenAutoplaySettings = useConfigurationStore((state) => state.shouldOpenAutoplaySettings)

    useEffect(() => {
        if (selectedAutoplayIndex === -1) {
            updateSelectedAutoplayIndex(0)
        }
    }, [selectedAutoplayIndex])

    const startAutoplay = (index: number) => {
        gameAnalytics.autoplayStarted(autoplayOptions[index], 'settings')
        gameAnalytics.setBetTrigger('autoplay')

        const autoplayButtonClickedMessage: AutoplayButtonClickedMessage = {
            type: GameUIMessageType.AUTOPLAY_BUTTON_CLICKED,
            payload: index,
        }

        window.postMessage(autoplayButtonClickedMessage, '*')

        updateAutoplayMode(true)
        updateAutoplayCount(autoplayOptions[index] !== -1 ? autoplayOptions[index] - 1 : autoplayOptions[index])

        setIsMenuOpen(false)
        playEngineSound(SOUNDS.SPIN)
    }

    const handleButtonClick = (index: number) => {
        updateSelectedAutoplayIndex(index)
        playEngineSound(SOUNDS.SPIN)
    }

    // Open the Ultra Play feature and close the menu (same entry as the autoplay-options modal).
    const handleGoToUltraPlay = () => {
        openUltraPlay()
        setIsMenuOpen(false)
        playEngineSound(SOUNDS.SHOW_DENOMINATIONS)
    }

    const goToUltraPlayButtonRef = useRef<HTMLSpanElement>(null)
    const goToUltraPlayButtonlabel = tUltra('gameui.ultraplay.goToUltraPlay')

    useShrinkTextToFit({
        refresh: [goToUltraPlayButtonlabel],
        designFontSize: 30,
        bounds: { width: 240 },
        textRef: goToUltraPlayButtonRef,
    })

    const renderButtons = () => (
        <>
            <div className="flex gap-3 w-full flex-wrap max-w-[728px] mx-auto justify-center">
                {buttons.map((b, i) => {
                    return (
                        <AutoplaySettingsOptionButton
                            key={b.spinsAmount}
                            spins={b.spinsAmount}
                            label={b.label}
                            isActive={selectedAutoplayIndex === i}
                            onClick={() => handleButtonClick(i)}
                            id={`autoplay-button-spins-${b.spinsAmount}`}
                        />
                    )
                })}
            </div>

            <div className="flex justify-center">
                <PlayNowButton onClick={() => startAutoplay(selectedAutoplayIndex)} id="autoplay-play-now-button" />
            </div>
        </>
    )

    const renderAutoplaySettings = () => (
        <>
            <div className="w-full max-w-[728px] mx-auto">
                <h3 className="text-white text-[38px] font-black leading-none" id="stop-autoplay-title">
                    {t('gameui.stopAutoplay')}
                </h3>
            </div>

            <div className="flex flex-col gap-5 w-full max-w-[728px] mx-auto">
                {items.map((i) => {
                    return <AutoplaySliderItem key={i.title} item={i} currency={currency} id="autoplay-slider-item" />
                })}
            </div>
        </>
    )

    return (
        <div
            className="flex flex-col w-full max-w-[1052px] px-10 pb-6 mx-auto gap-12 touch-auto [&_div]:touch-auto"
            id="autoplay-settings"
        >
            {viewport !== ViewportType.MOBILE_PORTRAIT && renderButtons()}

            {!shouldOpenAutoplaySettings ? renderAutoplaySettings() : null}

            {viewport === ViewportType.MOBILE_PORTRAIT && renderButtons()}

            {/* Ultra Play entry — only when the feature is enabled. Spacing per design: 60px above the
                logo (12px here + parent gap-12), 55px logo→text, 36px text→button, 75px below. */}
            {isUltraPlayAvailable && shouldShowSpeedButton && (
                <div className="w-full max-w-[728px] mx-auto mt-[12px] mb-[75px] flex flex-col items-center">
                    <img src={ultraPlayLogo} alt="Ultra Play" className="w-[340px] h-[40px]" />

                    <p className="self-stretch mt-[55px] font-roboto font-normal text-[32px] leading-none text-white">
                        {tUltra('gameui.ultraplay.shortDescription')}
                    </p>

                    <button
                        id="autoplay-go-to-ultra-play"
                        onClick={hasActiveBonus ? undefined : handleGoToUltraPlay}
                        className={cn(
                            'relative mt-[36px] flex h-[88px] w-[391px] items-center justify-center',
                            hasActiveBonus && 'opacity-50 pointer-events-none'
                        )}
                    >
                        <img
                            src={playNowButtonBg}
                            alt="Go to Ultra Play button background"
                            className="absolute inset-0 w-full h-full object-fill"
                            draggable="false"
                        />
                        <span
                            ref={goToUltraPlayButtonRef}
                            className="relative z-10 max-w-[260px] whitespace-pre-line text-center text-[30px] font-bold leading-[34px] text-white uppercase drop-shadow-text"
                        >
                            {tUltra('gameui.ultraplay.goToUltraPlay')}
                        </span>
                    </button>
                </div>
            )}
        </div>
    )
}

export default AutoplaySettings
